/* Evereve-spec filter behaviors, layered on Symmetry's FilterContainer.
   - Mobile (<=999px): filter changes inside the drawer are STAGED — Symmetry's
     auto-apply change handler is suppressed; View Results applies the form.
   - Drawer a11y: Escape closes, body scroll locks, focus is trapped and
     restored to the Filter By trigger on close.
   - Sort sheet: close button + Escape support for the link-dropdown.
   - Brand facets: View More / View Less truncation toggle.
   - Desktop (>=1000px): apply-on-change is DEBOUNCED. Stock Symmetry fires one
     AJAX request per change; each returns the whole collection page (~444KB
     measured), so ticking three boxes cost three round-trips of ~0.6s. The
     change is swallowed and replayed once the user stops clicking, and since
     the request serializes the whole form, the single replay applies every
     selection made during the window.
   Desktop AJAX/URL-state handling is otherwise stock Symmetry. */

(function () {
  const MOBILE = window.matchMedia('(max-width: 999.98px)');

  function initContainer(container) {
    if (!container || container.dataset.eeInit === 'true') return;
    container.dataset.eeInit = 'true';

    const filters = container.querySelector('.filters');
    if (!filters) return;

    let lastTrigger = null;
    let staged = false;

    // --- Stage changes on mobile: swallow 'change' before FilterContainer's
    // delegated listener (capture phase on the container ancestor). ---
    document.addEventListener(
      'change',
      (evt) => {
        if (!MOBILE.matches) return;
        if (!filters.contains(evt.target)) return;
        if (!evt.target.closest('#CollectionFilterForm')) return;
        staged = true;
        evt.stopPropagation(); // FilterContainer listens on the element below document
      },
      true
    );

    // --- Debounce apply-on-change on desktop. Same capture trick as the
    // mobile staging above: swallow the change before FilterContainer sees it,
    // then replay one synthetic change once the clicking stops. `replaying`
    // lets exactly that one event through. ---
    const APPLY_DELAY = 300;
    let applyTimer = null;
    let replaying = false;

    document.addEventListener(
      'change',
      (evt) => {
        if (MOBILE.matches) return; // mobile stages via View Results instead
        if (!filters.contains(evt.target)) return;
        if (!evt.target.closest('#CollectionFilterForm')) return;
        if (replaying) {
          replaying = false;
          return;
        }
        evt.stopPropagation();
        const target = evt.target;
        clearTimeout(applyTimer);
        applyTimer = setTimeout(() => {
          replaying = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }, APPLY_DELAY);
      },
      true
    );

    // View Results: apply staged filters via the form's submit path (AJAX)
    const viewResults = filters.querySelector('[data-view-results]');
    if (viewResults) {
      viewResults.addEventListener('click', () => {
        const form = filters.querySelector('#CollectionFilterForm');
        staged = false;
        closeDrawer();
        if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    // --- Drawer open/close bookkeeping ---
    function drawerIsOpen() {
      return container.classList.contains('filter-container--show-filters-mobile');
    }
    function closeDrawer() {
      if (!drawerIsOpen()) return;
      const closer = filters.querySelector('.filters__close');
      if (closer) closer.click(); // reuse theme's toggle handler
    }
    function onDrawerToggle() {
      const open = drawerIsOpen() && MOBILE.matches;
      document.body.classList.toggle('ee-drawer-lock', open);
      if (open) {
        const first = filters.querySelector('.filters__close');
        if (first) first.focus();
      } else if (lastTrigger) {
        lastTrigger.focus();
        lastTrigger = null;
      }
    }
    // track the trigger for focus restore
    document.addEventListener('click', (evt) => {
      const t = evt.target.closest('[data-toggle-filters]');
      if (t && !drawerIsOpen()) lastTrigger = t;
      // class flips synchronously in the theme handler; check after
      setTimeout(onDrawerToggle, 0);
    });

    // Escape closes drawer or sort sheet
    document.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape') return;
      if (drawerIsOpen()) {
        closeDrawer();
        return;
      }
      const openSort = document.querySelector('.ee-toolbar__sort .link-dropdown__button[aria-expanded="true"]');
      if (openSort) openSort.click();
    });

    // Focus trap while drawer is open
    filters.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Tab' || !drawerIsOpen() || !MOBILE.matches) return;
      const focusables = filters.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    });

    // --- Brand View More / View Less ---
    filters.addEventListener('click', (evt) => {
      const btn = evt.target.closest('[data-view-more]');
      if (!btn) return;
      const group = btn.closest('.filter-group--truncatable');
      const expanded = group.classList.toggle('filter-group--expanded');
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      btn.querySelector('[data-view-more-label]').textContent = expanded ? 'View Less' : 'View More';
    });

    // --- Sort sheet close button ---
    document.addEventListener('click', (evt) => {
      const closeBtn = evt.target.closest('[data-close-sort]');
      if (!closeBtn) return;
      const btn = closeBtn.closest('link-dropdown').querySelector('.link-dropdown__button');
      if (btn && btn.getAttribute('aria-expanded') === 'true') btn.click();
    });
  }

  function boot() {
    document.querySelectorAll('filter-container').forEach(initContainer);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
